import React, { useRef, useState } from 'react';
import * as _ from 'lodash';
import '@mescius/wijmo.styles/wijmo.css';
import '@mescius/wijmo.cultures/wijmo.culture.ja';
import * as wjGrid from '@mescius/wijmo.grid';
import { FlexGrid, FlexGridCellTemplate } from '@mescius/wijmo.react.grid';
import { CollectionView } from '@mescius/wijmo';

import { validationRegexByJoi } from '../../utilities/commonValidation';
import { VEHICLE_NUMBER_COLUMNS } from '../../utilities/constWijmo';
import { API } from '../../utilities/constants';
import { messageUtility } from '../../utilities/messageUtility';
import authProvider, { needTokenUpdate } from '../../utilities/authProvider';
import { schema } from '../../utilities/gatePassageUtil';
import regex from '../../utilities/validateRegex';
import SessionStorageService from '../../services/utilities/SessionStorageService';
import { Description } from '../Description';
import Joi from 'joi-browser';

const MAX_ROW = 250;

export const VehicleNumberDialog = ({ dialog }) => {
  const [dataSource, setDataSource] = useState([]);
  const [totalInvalidCell, setTotalInvalidCell] = useState(0);
  const [isEditing, setEditing] = useState(false);
  const grid = useRef();

  const initializeGrid = flexGrid => {
    grid.current = flexGrid;
    flexGrid.rowHeaders.columns.defaultSize = 40;
    flexGrid.keyActionEnter = wjGrid.KeyAction.CycleEditable;

    handleSetDataSource(_.cloneDeep(dialog.vehicleNumbers));
  };

  const cellEditEnded = (ctl, cellRangeEventArgs) => {
    ctl.errorTip.hide();
    const rowData = ctl.rows[cellRangeEventArgs.row];
    if (rowData) {
      const item = rowData.dataItem;
      handleSplitValue(item);
    }
  };

  const handleSplitValue = dataItem => {
    const { designated_number } = dataItem;
    const splitValue = designated_number.replace(/[-・]/g, '');
    dataItem.designated_number = handleTrimValue(designated_number);
    if (regex.pattern.integerNoSign.test(splitValue)) {
      dataItem.designated_number = handleTrimValue(splitValue);
    }
  };

  const handleTrimValue = text => {
    const value = text.replace(/\s/g, '');
    return value;
  };

  const handleCountErrorValidate = ctl => {
    let errorCount = 0;
    ctl.collectionView.items.forEach(item => {
      ctl.columns.forEach(col => {
        if (ctl.collectionView.getError(item, col.binding)) {
          errorCount++;
        }
      });
    });
    setTotalInvalidCell(errorCount);
  };

  const handleValidate = (item, colname) => {
    if (item && item[colname]) {
      item[colname] = handleTrimValue(item[colname]);
    }
    const { messages } = validationRegexByJoi(Joi.object().keys(schema), item);
    const value = _.find(_.values(item), val => !!handleTrimValue(val));
    if (value) {
      if (_.isEmpty(messages)) {
        let msgDuplicate = '';
        let countDuplicate = 0;
        _.forEach(grid.current.itemsSource._src, data => {
          if (_.isEqual(data, item)) {
            countDuplicate++;
            if (countDuplicate > 1) {
              msgDuplicate =
                '重複している車両番号がありますので、片方を削除してください。';
              return false;
            }
          }
        });
        return msgDuplicate;
      }
      return messages[colname];
    }
  };

  const handleMergeData = vehicleNumbers => {
    const defaultData = [];
    for (let index = 0; index < MAX_ROW - _.size(vehicleNumbers); index++) {
      defaultData.push({
        area: '',
        class_number: '',
        hiragana: '',
        designated_number: '',
      });
    }
    return [...vehicleNumbers, ...defaultData];
  };

  const handleSetVehicleNumbers = () => {
    if (!isEditing) {
      const vehicleNumbers = _.filter(dataSource.items, item => !!item.area);
      dialog.callback.save(vehicleNumbers);
    }
  };

  const handleSetDataSource = (vehicleNumbers, clear = true) => {
    const dataMerge = handleMergeData(vehicleNumbers);
    const dataMap = _.map(dataMerge, data => _.omit(data, 'id'));
    const itemSource = new CollectionView(dataMap, {
      getError: (item, prop) => handleValidate(item, prop),
    });
    setDataSource(itemSource);
    grid.current.select(0, 0);
    grid.current.focus();
    if (clear) {
      setTotalInvalidCell(0);
    }
  };

  const handleDownloadTemplateVehicle = async () => {
    let token = SessionStorageService.getToken();
    if (needTokenUpdate(token)) {
      token = await authProvider.instance.silentTokenUdate();
    }
    const apiPath = `api/private/fleet_mgmt/vehicle_numbers/download_template.xlsx?excel_token=${token}`;
    window.location.href = `${API.endpoint}/${apiPath}`;
  };

  return (
    <div className='VehicleNumberDialog'>
      <div className='VehicleNumberDialog__title'>
        {messageUtility.getMessage('E-001-435')}
      </div>
      <div className='VehicleNumberDialog__body'>
        <Description>
          直接入力するか、Excelなどからセルをコピー＆ペーストする事で車両の登録ができます。最大登録件は250件です。
        </Description>
        <div className='VehicleNumberDialog__body--actions'>
          {
            <div className='VehicleNumberDialog__body--error'>
              {!!totalInvalidCell && (
                <React.Fragment>
                  エラーが{totalInvalidCell}
                  件あります。入力内容を確認してください。
                </React.Fragment>
              )}
            </div>
          }
          <ul className='btn-menu'>
            <li className='btn-menu__item'>
              <button
                className='btn--sub btn-export'
                onClick={handleDownloadTemplateVehicle}
              >
                Excelテンプレート
              </button>
            </li>
            <li className='btn-menu__item'>
              <button
                className='btn--sub'
                onClick={() => handleSetDataSource([], true)}
              >
                {messageUtility.getMessage('I-000-211')}
              </button>
            </li>
          </ul>
        </div>
        <FlexGrid
          imeEnabled={true}
          itemsSource={dataSource}
          columnGroups={VEHICLE_NUMBER_COLUMNS}
          autoGenerateColumns={false}
          showMarquee={true}
          allowSorting={false}
          allowDragging={0}
          allowResizing={false}
          quickAutoSize={true}
          initialized={initializeGrid}
          cellEditEnded={cellEditEnded}
          beginningEdit={() => setEditing(true)}
          pasted={ctl => handleCountErrorValidate(ctl)}
          refreshed={ctl => {
            handleCountErrorValidate(ctl);
            setEditing(false);
          }}
          validateEdits={false}
        >
          <FlexGridCellTemplate
            cellType='RowHeader'
            template={context => Number(context.row.index) + 1}
          />
        </FlexGrid>
      </div>
      <div className='VehicleNumberDialog__footer'>
        <ul className='btn-menu'>
          <li className='btn-menu__item'>
            <button className='btn--sub' onClick={dialog.callback.close}>
              {messageUtility.getMessage('I-000-017')}
            </button>
          </li>
          <li className='btn-menu__item'>
            <button
              className={`btn ${
                totalInvalidCell || isEditing ? 'btn--disabled' : 'btn-primary'
              }`}
              onClick={handleSetVehicleNumbers}
              disabled={!!totalInvalidCell || isEditing}
            >
              {messageUtility.getMessage('I-000-088')}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/modals/VehicleNumberDialog.jsx